import { includes } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import parentMessages from '@/pages/SpaceAnalyticsPage/messages'
import DifferenceTool from '@/pages/SpaceAnalyticsPage/util/DifferenceTool'

import messages from './messages'

const METRIC_MAP = {
  TOTAL_SPACE_MEMBERS: {
    key: 'totalSpaceMembers',
    titleId: messages.totalSpaceMembersMetricTitle,
    backgroundColor: '#eff5fb',
    headerColor: '#878b8f',
    metricColor: '#000000'
  },
  UNIQUE_ENGAGED_MEMBERS: {
    key: 'engagedUsers',
    titleId: messages.uniqueActiveUsersMetricTitle,
    backgroundColor: '#00988A',
    headerColor: '#ffffff',
    metricColor: '#ffffff',
    isPlotted: true
  },
  ENGAGED_NOW: {
    key: 'engagedNow',
    titleId: messages.activeNowMetricTitle,
    backgroundColor: '#eff5fb',
    headerColor: '#878b8f',
    metricColor: '#000000'
  },
  SESSIONS: {
    key: 'uniqueSessions',
    titleId: messages.sessionsMetricTitle,
    backgroundColor: '#551da2',
    headerColor: '#ffffff',
    metricColor: '#ffffff',
    isPlotted: true
  },
  ENGAGEMENT: {
    key: 'totalEngagements',
    titleId: messages.totalEngagementsMetricTitle,
    backgroundColor: '#EA8C0B',
    headerColor: '#ffffff',
    metricColor: '#ffffff',
    isPlotted: true
  }
}

const chartColor = '#bababa'

function UserActivitySection(props: any) {
  const intl = useIntl()

  const [state, setState] = useState({
    activeChartLine: ''
  })

  const shownXAxisValues = useRef<number[]>([])

  const buildMetricCardData = ({
    totalEngagedUsers,
    totalActivity,
    totalEngagements,
    previousTotalEngagedUsers,
    previousTotalActivity,
    previousTotalEngagements
  }: any) => {
    return [
      {
        ...METRIC_MAP.UNIQUE_ENGAGED_MEMBERS,
        title: intl.formatMessage(METRIC_MAP.UNIQUE_ENGAGED_MEMBERS.titleId),
        value: totalEngagedUsers,
        ...((previousTotalEngagedUsers || previousTotalEngagedUsers === 0) && { difference: previousTotalEngagedUsers })
      },
      {
        ...METRIC_MAP.ENGAGEMENT,
        title: intl.formatMessage(METRIC_MAP.ENGAGEMENT.titleId),
        value: totalEngagements,
        ...((previousTotalEngagements || previousTotalEngagements === 0) && { difference: previousTotalEngagements })
      },
      {
        ...METRIC_MAP.SESSIONS,
        title: intl.formatMessage(METRIC_MAP.SESSIONS.titleId),
        value: totalActivity,
        ...((previousTotalActivity || previousTotalActivity === 0) && { difference: previousTotalActivity })
      }
    ]
  }

  const buildNonPlottedMetricCardData = ({ totalEngagedNow, totalSpaceMembers, previousTotalSpaceMembers }: any) => {
    return [
      {
        ...METRIC_MAP.TOTAL_SPACE_MEMBERS,
        title: intl.formatMessage(METRIC_MAP.TOTAL_SPACE_MEMBERS.titleId),
        value: totalSpaceMembers,
        ...((previousTotalSpaceMembers || previousTotalSpaceMembers === 0) && { difference: previousTotalSpaceMembers })
      },
      {
        ...METRIC_MAP.ENGAGED_NOW,
        title: intl.formatMessage(METRIC_MAP.ENGAGED_NOW.titleId),
        value: totalEngagedNow
      }
    ]
  }

  const determineXAxisTicksToShow = (timeLineData: any) => {
    if (timeLineData.length <= 0) return []
    const divider = 5
    const arraySeparator = Math.round(timeLineData.length / divider)
    const shownValues = []
    const numberOfTicks = 4

    for (let i = 1; i <= numberOfTicks; i++) {
      const index = i * arraySeparator
      if (index < timeLineData.length - 1) shownValues.push(index)
    }

    shownXAxisValues.current = shownValues
  }

  const getDisplayName = (id: any, isUpperCase: any) => {
    let result
    switch (id) {
      case METRIC_MAP.UNIQUE_ENGAGED_MEMBERS.key: {
        result = intl.formatMessage(METRIC_MAP.UNIQUE_ENGAGED_MEMBERS.titleId)
        break
      }
      case METRIC_MAP.SESSIONS.key: {
        result = intl.formatMessage(METRIC_MAP.SESSIONS.titleId)
        break
      }
      case METRIC_MAP.ENGAGEMENT.key: {
        result = intl.formatMessage(METRIC_MAP.ENGAGEMENT.titleId)
        break
      }
      default: {
        result = intl.formatMessage(messages.missingName)
      }
    }
    return isUpperCase ? result.toUpperCase() : result
  }

  const renderLegend = ({ payload }: any) => {
    return (
      <div className="chart activity over-time legend holder">
        {payload.map((item: any) => {
          return (
            <div key={item.dataKey} className="item-holder">
              <div style={{ backgroundColor: item.color }} />
              <span>{getDisplayName(item.dataKey, false)}</span>
            </div>
          )
        })}
      </div>
    )
  }

  const renderTooltip = ({ active, payload }: any) => {
    if (active && payload[0]) {
      return (
        <div className="chart activity over-time floating-tooltip">
          <div className="header-holder">
            <h3>{payload[0].payload.date}</h3>
          </div>
          <div className="holder">
            <div className="metric-holder">
              <div className="metric-header-holder">
                <p>{getDisplayName(payload[0]?.dataKey, true)}</p>
              </div>
              <h3 style={{ color: payload[0]?.color }}>{payload[0].payload.engagedUsers?.toLocaleString() || 0}</h3>
            </div>
            <div className="divider" />
            <div className="metric-holder">
              <div className="metric-header-holder">
                <p className="metric-header">{getDisplayName(payload[1]?.dataKey, true)}</p>
              </div>
              <h3 style={{ color: payload[1]?.color }}>
                {payload[1]?.payload.totalEngagements?.toLocaleString() || 0}
              </h3>
            </div>
            <div className="divider" />
            <div className="metric-holder">
              <div className="metric-header-holder">
                <p className="metric-header">{getDisplayName(payload[2]?.dataKey, true)}</p>
              </div>
              <h3 className="total">{payload[2]?.payload.uniqueSessions?.toLocaleString() || 0}</h3>
            </div>
          </div>
        </div>
      )
    }
  }

  const customXAxisTick = (props: any) => {
    const { x, y, index, payload, width, height } = props
    if (!includes(shownXAxisValues.current, index)) return
    return (
      <g fill={chartColor} stroke="1" transform={`translate(${x},${y - 8})`}>
        <line
          className="recharts-cartesian-axis-tick-line"
          width={width}
          height={height}
          x1={0}
          y1={0}
          x2={0}
          y2={6}
          stroke={chartColor}
          strokeWidth={3}
        />
        <text x={0} y={0} dy={25} textAnchor="middle" fill={chartColor}>
          {payload.value}
        </text>
      </g>
    )
  }

  /**
   * Usage over time includes both activity and engagement metrics.
   * This function merges the two (by common time value) and formats the data in the required structure for the line chart
   *
   * @param activity
   * @param engagements
   */
  function mergeAndFormatUsageOverTimeData(
    activity: Array<{ time: number; totalUtterances: number; uniqueSessions: number; uniqueUsers: number }>,
    engagements: Array<{ time: number; engagedUsers: number; totalEngagements: number }>
  ) {
    const combinedData = [...activity, ...engagements]
      .toSorted((a, b) => a.time - b.time)
      .map(item => {
        return {
          ...item,
          date: moment(item.time).format(props.chartFormat)
        }
      })

    const groupedByDate = Object.groupBy(combinedData, r => r.date)

    const groupedTotals = Object.entries(groupedByDate).map(([date, items]) => {
      const totals = { date, engagedUsers: 0, totalEngagements: 0, uniqueSessions: 0, sessions: 0 }

      items?.forEach(item => {
        if ('engagedUsers' in item) totals['engagedUsers'] += item.engagedUsers
        if ('totalEngagements' in item) totals['totalEngagements'] += item.totalEngagements
        if ('uniqueSessions' in item) totals['uniqueSessions'] += item.uniqueSessions
      })

      return totals
    })

    return groupedTotals
  }

  const handleOnMouseEnter = (e: any) => {
    setState({ activeChartLine: e.currentTarget.id })
  }

  const handleOnMouseLeave = () => {
    setState({ activeChartLine: '' })
  }

  const determineLineChartOpacity = (line: any, activeChartLine: any) => {
    if (line === activeChartLine) {
      return { transition: '500ms', opacity: '80%' }
    } else if (activeChartLine === '') {
      return { transition: '500ms', opacity: '80%' }
    } else {
      return { transition: '500ms', opacity: '15%' }
    }
  }

  const {
    totalEngagedUsers,
    totalActivity,
    userActivity,
    engagementOverTime,
    totalEngagedNow,
    totalSpaceMembers,
    totalEngagements,
    previousTotalSpaceMembers,
    previousTotalEngagedUsers,
    previousTotalActivity,
    previousTotalEngagements
  } = props

  const { activeChartLine } = state

  const formattedUserActivityData = mergeAndFormatUsageOverTimeData(userActivity, engagementOverTime)
  determineXAxisTicksToShow(formattedUserActivityData)

  const nonPlottedMetricCardMetrics = buildNonPlottedMetricCardData({
    totalEngagedNow,
    totalSpaceMembers,
    previousTotalSpaceMembers
  })

  const metricCardMetrics = buildMetricCardData({
    totalEngagedUsers,
    totalActivity,
    totalEngagements,
    previousTotalEngagedUsers,
    previousTotalActivity,
    previousTotalEngagements
  })

  return (
    <div className="analytics-section">
      <div className="metric-card-subheader">
        <h3>
          <FormattedMessage {...messages.userActivitySectionTitle} />
        </h3>
        <span className="sub-text">
          <FormattedMessage {...messages.titleSubText} />
        </span>
      </div>
      <div>
        <div className="widget-wrapper">
          {nonPlottedMetricCardMetrics.map(metric => {
            return (
              <div
                id={metric.key}
                className="metric-card"
                key={metric.key}
                style={{
                  margin: 'auto',
                  backgroundColor: metric.backgroundColor,
                  cursor: metric.isPlotted && 'pointer'
                }}
              >
                <div className="metric-card-header">
                  <p style={{ color: metric.headerColor }}>{metric.title.toUpperCase()}</p>
                </div>
                {metric.difference || metric.difference === 0 ? (
                  <div className="metric-card-body percentage-visible">
                    <div className="metric">
                      <h3 style={{ color: metric.metricColor }}>{metric.value.toLocaleString()}</h3>
                    </div>
                    <div className="divider" style={{ backgroundColor: metric.metricColor }} />
                    <DifferenceTool
                      currentTotal={metric.value}
                      previousTotal={metric.difference}
                      color={metric.metricColor}
                      className="percentage"
                    />
                  </div>
                ) : (
                  <div className="metric-card-body">
                    <h3 style={{ color: metric.metricColor }}>{metric.value.toLocaleString()}</h3>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
      {formattedUserActivityData.length > 0 ? (
        <div>
          <div className="widget-wrapper">
            {metricCardMetrics.map(metric => {
              return (
                <div
                  id={metric.key}
                  className="metric-card"
                  key={metric.key}
                  style={{
                    margin: 'auto',
                    backgroundColor: metric.backgroundColor,
                    cursor: metric.isPlotted && 'pointer'
                  }}
                  onMouseEnter={metric.isPlotted && handleOnMouseEnter}
                  onMouseLeave={metric.isPlotted && handleOnMouseLeave}
                >
                  <div className="metric-card-header">
                    <p style={{ color: metric.headerColor }}>{metric.title.toUpperCase()}</p>
                  </div>
                  {metric.difference || metric.difference === 0 ? (
                    <div className="metric-card-body percentage-visible">
                      <div className="metric">
                        <h3 style={{ color: metric.metricColor }}>{metric.value.toLocaleString()}</h3>
                      </div>
                      <div className="divider" style={{ backgroundColor: metric.metricColor }} />
                      <DifferenceTool
                        currentTotal={metric.value}
                        previousTotal={metric.difference}
                        color={metric.metricColor}
                        className="percentage"
                      />
                    </div>
                  ) : (
                    <div className="metric-card-body">
                      <h3 style={{ color: metric.metricColor }}>{metric.value.toLocaleString()}</h3>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          <ResponsiveContainer width="100%" height={390}>
            <LineChart data={formattedUserActivityData} margin={{ top: 5, right: 5, left: -5, bottom: 20 }}>
              <XAxis
                dataKey="date"
                axisLine={{ stroke: chartColor }}
                tickLine={false}
                tick={customXAxisTick}
                interval={0}
              />
              <Tooltip content={renderTooltip} />
              <Legend content={renderLegend} />
              <CartesianGrid horizontal={false} stroke="#f5f5f5" />
              <YAxis axisLine={{ stroke: chartColor }} tickLine={false} tick={{ stroke: chartColor }} />
              <Line
                type="monotone"
                dataKey="engagedUsers"
                stroke="#00988A"
                strokeWidth="3px"
                dot={false}
                style={determineLineChartOpacity(METRIC_MAP.UNIQUE_ENGAGED_MEMBERS.key, activeChartLine)}
              />
              <Line
                type="monotone"
                dataKey="totalEngagements"
                stroke="#EA8C0B"
                strokeWidth="3px"
                dot={false}
                style={determineLineChartOpacity(METRIC_MAP.ENGAGEMENT.key, activeChartLine)}
              />
              <Line
                type="monotone"
                dataKey="uniqueSessions"
                stroke="#551da2"
                strokeWidth="3px"
                dot={false}
                style={determineLineChartOpacity(METRIC_MAP.SESSIONS.key, activeChartLine)}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="flex-center">
          <FormattedMessage {...parentMessages.noActivityMetrics} />
        </p>
      )}
    </div>
  )
}

UserActivitySection.displayName = 'UserActivitySection'

UserActivitySection.propTypes = {
  totalEngagedUsers: PropTypes.number.isRequired,
  totalActivity: PropTypes.number.isRequired,
  totalEngagedNow: PropTypes.number.isRequired,
  totalSpaceMembers: PropTypes.number.isRequired,
  userActivity: PropTypes.array.isRequired,
  chartFormat: PropTypes.string.isRequired,
  totalEngagements: PropTypes.number.isRequired,
  engagementOverTime: PropTypes.array.isRequired,
  previousTotalEngagedUsers: PropTypes.any,
  previousTotalActivity: PropTypes.any,
  previousTotalEngagements: PropTypes.any,
  previousTotalSpaceMembers: PropTypes.any
}

export default UserActivitySection
